import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { plural, WORDS } from '../../lib/format';
import { downscaleFile } from '../../lib/imageResize';
import { useAuth, type SessionUser } from '../../auth/AuthProvider';
import { Icon, type IconName } from '../../components/Icon';
import { Badge } from '../../components/ui';
import { Avatar } from '../../components/People';
import { ImageCropModal } from '../../components/ImageCropModal';
import { PhoneField } from '../../components/PhoneField';
import { PasswordInput } from '../../components/PasswordInput';
import { useToast } from '../../components/Toast';

const GENDER_LABELS: Record<string, string> = {
  male: 'ذكر',
  female: 'أنثى',
  other: 'أفضّل عدم الإفصاح',
};
const LOCALE_LABELS: Record<string, string> = { ar: 'العربية', en: 'English' };

function formatBirthday(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('ar-EG', { day: 'numeric', month: 'long', year: 'numeric' });
}

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'مالك الحساب',
  ADMIN: 'مدير النظام',
  MANAGER: 'مدير',
  SALES: 'مبيعات',
  EMPLOYEE: 'موظف',
};

/** يفصل الاسمَ الكاملَ إلى أوّلَ وأخير على أوّل مسافة. */
function splitName(full: string): { first: string; last: string } {
  const parts = full.trim().split(/\s+/);
  return { first: parts.shift() ?? '', last: parts.join(' ') };
}

/**
 * الملفُّ الشخصيّ — قسمُ «حسابي» على طراز حساب جوجل.
 *
 * ترويسةٌ بغلافٍ وصورةٍ يُبدَّلان من أيقونةٍ عليهما، ثمّ أقسامٌ مفصولة:
 * «المعلومات الشخصية» نموذجًا (اسمٌ أوّلُ وأخير وهاتف)، و«الأمان» لكلمة
 * المرور، فمركزُ الاتصال. والاشتراكُ صفحتُه المستقلّة وحدَها لا يُكرَّر هنا.
 */
export function Profile() {
  const { session, atLeast } = useAuth();
  if (!session) return null;

  return (
    <div className="page">
      <ProfileHero />

      <h2 className="acc-sec-title">
        <span className="acc-sec-ic">
          <Icon name="user" size={17} />
        </span>
        المعلومات الشخصية
      </h2>
      <PersonalInfoCard />

      <h2 className="acc-sec-title">
        <span className="acc-sec-ic">
          <Icon name="settings" size={17} />
        </span>
        الأمان
      </h2>
      <PasswordCard />
      <div className="mt-4">
        <TwoFactorCard />
      </div>
      <div className="mt-4">
        <RecoveryEmailCard />
      </div>

      {atLeast('OWNER') && (
        <>
          <h2 className="acc-sec-title">
            <span
              className="acc-sec-ic"
              style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}
            >
              <Icon name="trash" size={17} />
            </span>
            منطقة الخطر
          </h2>
          <DeleteAccountCard />
        </>
      )}
    </div>
  );
}

/* ---------------- ترويسة الملفّ: الغلاف والصورة ---------------- */

function ProfileHero() {
  const { session, reload } = useAuth();
  const toast = useToast();
  const user = session!.user;
  const tenant = session!.tenant;

  const avatarInput = useRef<HTMLInputElement>(null);
  const coverInput = useRef<HTMLInputElement>(null);
  const [crop, setCrop] = useState<
    null | { kind: 'avatar' | 'cover'; srcUrl: string; original: string }
  >(null);
  const [avaMenu, setAvaMenu] = useState(false);
  const [coverMenu, setCoverMenu] = useState(false);

  const save = useMutation({
    mutationFn: (body: {
      avatarUrl?: string | null;
      coverUrl?: string | null;
      avatarOriginal?: string | null;
      coverOriginal?: string | null;
    }) => api('/auth/me', { method: 'PATCH', body }),
    onSuccess: async () => {
      await reload();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  /* ملفٌّ جديد: يُصغَّر أصلُه ليُحفظ، ثمّ يُفتح القاصُّ عليه. */
  const onFilePicked = async (kind: 'avatar' | 'cover', f: File) => {
    try {
      const original = await downscaleFile(f, kind === 'cover' ? 1920 : 1024, 0.85);
      setCrop({ kind, srcUrl: original, original });
    } catch {
      toast('تعذّرت قراءة الصورة');
    }
  };

  const onCropDone = async (dataUrl: string) => {
    const kind = crop!.kind;
    const original = crop!.original;
    setCrop(null);
    try {
      await save.mutateAsync(
        kind === 'avatar'
          ? { avatarUrl: dataUrl, avatarOriginal: original }
          : { coverUrl: dataUrl, coverOriginal: original },
      );
      toast(kind === 'avatar' ? 'حُدّثت الصورة' : 'حُدّث الغلاف', 'success');
    } catch {
      /* الخطأ يظهر من onError. */
    }
  };

  const removeCover = async () => {
    try {
      await save.mutateAsync({ coverUrl: null, coverOriginal: null });
      toast('أُزيل الغلاف', 'success');
    } catch {
      /* onError. */
    }
  };

  const removeAvatar = async () => {
    setAvaMenu(false);
    try {
      await save.mutateAsync({ avatarUrl: null, avatarOriginal: null });
      toast('أُزيلت الصورة', 'success');
    } catch {
      /* onError. */
    }
  };

  const busy = save.isPending;

  return (
    <div className="acc-hero">
      <div className={`acc-cover${user.coverUrl ? ' has-cover' : ''}`}>
        {user.coverUrl && <img className="acc-cover-img" src={user.coverUrl} alt="" />}
        <div className="acc-cover-actions">
          <input
            ref={coverInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFilePicked('cover', f);
              if (coverInput.current) coverInput.current.value = '';
            }}
          />
          <button
            type="button"
            className="acc-cover-btn"
            title={user.coverUrl ? 'تعديل الغلاف' : 'إضافة غلاف'}
            aria-label={user.coverUrl ? 'تعديل الغلاف' : 'إضافة غلاف'}
            aria-haspopup="menu"
            aria-expanded={coverMenu}
            onClick={() => {
              if (user.coverUrl) setCoverMenu((v) => !v);
              else coverInput.current?.click();
            }}
            disabled={busy}
          >
            <Icon name="edit" size={15} />
          </button>

          {coverMenu && user.coverUrl && (
            <>
              <button
                type="button"
                className="acc-ava-scrim"
                aria-label="إغلاق"
                onClick={() => setCoverMenu(false)}
              />
              <div className="acc-ava-menu acc-cover-menu" role="menu">
                <button
                  type="button"
                  className="acc-ava-mi"
                  onClick={() => {
                    setCoverMenu(false);
                    const src = user.coverOriginal ?? user.coverUrl!;
                    setCrop({ kind: 'cover', srcUrl: src, original: src });
                  }}
                >
                  <Icon name="expand" size={15} />
                  ضبط موضع الغلاف
                </button>
                <button
                  type="button"
                  className="acc-ava-mi"
                  onClick={() => {
                    setCoverMenu(false);
                    coverInput.current?.click();
                  }}
                >
                  <Icon name="edit" size={15} />
                  تغيير بصورة أخرى
                </button>
                <button
                  type="button"
                  className="acc-ava-mi is-danger"
                  onClick={() => {
                    setCoverMenu(false);
                    void removeCover();
                  }}
                >
                  <Icon name="trash" size={15} />
                  إزالة الغلاف
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="acc-hero-body">
        <div className="acc-ava-ring">
          <Avatar name={user.name} src={user.avatarUrl} size={92} />
          <input
            ref={avatarInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFilePicked('avatar', f);
              if (avatarInput.current) avatarInput.current.value = '';
            }}
          />
          <button
            type="button"
            className="acc-ava-edit"
            title="الصورة الشخصية"
            aria-label="تعديل الصورة الشخصية"
            aria-haspopup="menu"
            aria-expanded={avaMenu}
            onClick={() => setAvaMenu((v) => !v)}
            disabled={busy}
          >
            <Icon name={busy ? 'refresh' : 'edit'} size={15} />
          </button>

          {avaMenu && (
            <>
              <button
                type="button"
                className="acc-ava-scrim"
                aria-label="إغلاق"
                onClick={() => setAvaMenu(false)}
              />
              <div className="acc-ava-menu" role="menu">
                {user.avatarUrl && (
                  <button
                    type="button"
                    className="acc-ava-mi"
                    onClick={() => {
                      setAvaMenu(false);
                      const src = user.avatarOriginal ?? user.avatarUrl!;
                      setCrop({ kind: 'avatar', srcUrl: src, original: src });
                    }}
                  >
                    <Icon name="expand" size={15} />
                    تعديل الصورة الحالية
                  </button>
                )}
                <button
                  type="button"
                  className="acc-ava-mi"
                  onClick={() => {
                    setAvaMenu(false);
                    avatarInput.current?.click();
                  }}
                >
                  <Icon name="edit" size={15} />
                  تغيير بصورة أخرى
                </button>
                {user.avatarUrl && (
                  <button
                    type="button"
                    className="acc-ava-mi is-danger"
                    onClick={() => void removeAvatar()}
                  >
                    <Icon name="trash" size={15} />
                    إزالة الصورة
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="acc-hero-id">
          <h1 className="acc-hero-name">
            {user.name}
            <span className="acc-role">
              <Icon name="diamond" size={12} />
              {ROLE_LABELS[user.role] ?? user.role}
            </span>
          </h1>
          <div className="acc-hero-meta">
            <span className="num" dir="ltr">
              <Icon name="mail" size={13} />
              {user.email}
            </span>
            {user.phone && (
              <span className="num" dir="ltr">
                <Icon name="phone" size={13} />
                {user.phone}
              </span>
            )}
            <span>
              <Icon name="building" size={13} />
              {tenant.name}
            </span>
          </div>
        </div>
      </div>

      {crop && (
        <ImageCropModal
          srcUrl={crop.srcUrl}
          title={crop.kind === 'avatar' ? 'ضبط الصورة الشخصية' : 'ضبط صورة الغلاف'}
          aspect={crop.kind === 'avatar' ? 1 : 4}
          round={crop.kind === 'avatar'}
          outputW={crop.kind === 'avatar' ? 256 : 1280}
          outputH={crop.kind === 'avatar' ? 256 : 320}
          onCancel={() => setCrop(null)}
          onDone={(dataUrl) => void onCropDone(dataUrl)}
        />
      )}
    </div>
  );
}

/* ---------------- المعلومات الشخصية: صفوفٌ على طراز جوجل ---------------- */

function PersonalInfoCard() {
  const { session, reload } = useAuth();
  const toast = useToast();
  const user = session!.user;
  const tenant = session!.tenant;
  const [open, setOpen] = useState<string | null>(null);
  const close = () => setOpen(null);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/auth/me', { method: 'PATCH', body }),
    onSuccess: async () => {
      await reload();
      toast('حُفظت بياناتك', 'success');
      close();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const birthdayText = formatBirthday(user.birthday);

  return (
    <div className="card">
      <div className="card-body">
        <div className="info-list">
          <InfoRow
            icon="user"
            label="الاسم"
            value={user.name}
            open={open === 'name'}
            onEdit={() => setOpen('name')}
            onClose={close}
            editor={
              <NameEditor
                user={user}
                pending={patch.isPending}
                onSave={(name) => patch.mutate({ name })}
                onCancel={close}
              />
            }
          />

          <InfoRow
            icon="users"
            label="الجنس"
            value={user.gender ? GENDER_LABELS[user.gender] : 'غير محدّد'}
            empty={!user.gender}
            open={open === 'gender'}
            onEdit={() => setOpen('gender')}
            onClose={close}
            editor={
              <ChoiceEditor
                value={user.gender ?? ''}
                options={[
                  { value: 'male', label: 'ذكر' },
                  { value: 'female', label: 'أنثى' },
                  { value: 'other', label: 'أفضّل عدم الإفصاح' },
                ]}
                pending={patch.isPending}
                onSave={(v) => patch.mutate({ gender: v || null })}
                onCancel={close}
              />
            }
          />

          <InfoRow
            icon="mail"
            label="البريد الإلكتروني"
            value={<span className="num" dir="ltr">{user.email}</span>}
            pending={user.pendingEmail ? `بانتظار تأكيد: ${user.pendingEmail}` : undefined}
            open={open === 'email'}
            onEdit={() => setOpen('email')}
            onClose={close}
            editor={<EmailChangeEditor currentEmail={user.email} onDone={close} />}
          />

          <InfoRow
            icon="phone"
            label="رقم الهاتف"
            value={user.phone ? <span className="num" dir="ltr">{user.phone}</span> : 'غير مضاف'}
            empty={!user.phone}
            open={open === 'phone'}
            onEdit={() => setOpen('phone')}
            onClose={close}
            editor={
              <PhoneEditor
                initial={user.phone ?? ''}
                pending={patch.isPending}
                onSave={(v) => patch.mutate({ phone: v || null })}
                onCancel={close}
              />
            }
          />

          <InfoRow
            icon="calendar"
            label="تاريخ الميلاد"
            value={birthdayText ?? 'غير محدّد'}
            empty={!birthdayText}
            open={open === 'birthday'}
            onEdit={() => setOpen('birthday')}
            onClose={close}
            editor={
              <DateEditor
                initial={user.birthday ? user.birthday.slice(0, 10) : ''}
                pending={patch.isPending}
                onSave={(v) => patch.mutate({ birthday: v || null })}
                onCancel={close}
              />
            }
          />

          <InfoRow
            icon="globe"
            label="اللغة"
            value={LOCALE_LABELS[user.locale ?? 'ar'] ?? 'العربية'}
            open={open === 'locale'}
            onEdit={() => setOpen('locale')}
            onClose={close}
            editor={
              <ChoiceEditor
                value={user.locale ?? 'ar'}
                options={[
                  { value: 'ar', label: 'العربية' },
                  { value: 'en', label: 'English' },
                ]}
                pending={patch.isPending}
                onSave={(v) => patch.mutate({ locale: v })}
                onCancel={close}
              />
            }
          />

          <InfoRow
            icon="building"
            label="الشركة"
            value={
              <>
                {tenant.name}{' '}
                <span className="tiny mute num" dir="ltr">
                  ({tenant.slug})
                </span>
              </>
            }
            readOnly
          />
        </div>
      </div>
    </div>
  );
}

/* صفٌّ واحد: أيقونةٌ وعنوانٌ وقيمة، وزرُّ تعديلٍ يفتح محرّرَه تحته. */
function InfoRow({
  icon,
  label,
  value,
  empty,
  pending,
  open,
  readOnly,
  onEdit,
  onClose,
  editor,
}: {
  icon: IconName;
  label: string;
  value: ReactNode;
  empty?: boolean;
  pending?: string;
  open?: boolean;
  readOnly?: boolean;
  onEdit?: () => void;
  onClose?: () => void;
  editor?: ReactNode;
}) {
  return (
    <div className="info-block">
      <div className="info-row">
        <span className="info-row-ic">
          <Icon name={icon} size={18} />
        </span>
        <div className="info-row-main">
          <div className="info-row-label">{label}</div>
          <div className={`info-row-value${empty ? ' is-empty' : ''}`}>{value}</div>
          {pending && <div className="info-row-pending">{pending}</div>}
        </div>
        {!readOnly &&
          (open ? (
            <button className="btn btn-sm btn-icon" onClick={onClose} aria-label="إغلاق">
              <Icon name="x" size={15} />
            </button>
          ) : (
            <button className="btn btn-sm btn-icon" onClick={onEdit} aria-label={`تعديل ${label}`}>
              <Icon name="edit" size={15} />
            </button>
          ))}
      </div>
      {open && editor && <div className="info-editor">{editor}</div>}
    </div>
  );
}

/* ----- محرّرات الصفوف ----- */

function EditorActions({
  onSave,
  onCancel,
  disabled,
  pending,
  saveLabel = 'حفظ',
}: {
  onSave: () => void;
  onCancel: () => void;
  disabled?: boolean;
  pending?: boolean;
  saveLabel?: string;
}) {
  return (
    <div className="info-editor-actions">
      <button className="btn btn-primary btn-sm" onClick={onSave} disabled={disabled || pending}>
        {pending ? 'جارٍ الحفظ' : saveLabel}
      </button>
      <button className="btn btn-sm" onClick={onCancel} disabled={pending}>
        إلغاء
      </button>
    </div>
  );
}

function NameEditor({
  user,
  pending,
  onSave,
  onCancel,
}: {
  user: SessionUser;
  pending: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const s = splitName(user.name);
  const [first, setFirst] = useState(s.first);
  const [last, setLast] = useState(s.last);
  const full = [first.trim(), last.trim()].filter(Boolean).join(' ');

  return (
    <>
      <div className="row" style={{ gap: 'var(--sp-3)' }}>
        <label className="field" style={{ flex: 1, minWidth: 140 }}>
          <span className="label">الاسم الأول</span>
          <input
            className="input"
            value={first}
            onChange={(e) => setFirst(e.target.value)}
            maxLength={60}
            autoFocus
          />
        </label>
        <label className="field" style={{ flex: 1, minWidth: 140 }}>
          <span className="label">اسم العائلة</span>
          <input
            className="input"
            value={last}
            onChange={(e) => setLast(e.target.value)}
            maxLength={60}
          />
        </label>
      </div>
      <EditorActions
        onSave={() => onSave(full)}
        onCancel={onCancel}
        disabled={full.length < 2}
        pending={pending}
      />
    </>
  );
}

function PhoneEditor({
  initial,
  pending,
  onSave,
  onCancel,
}: {
  initial: string;
  pending: boolean;
  onSave: (v: string) => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  return (
    <>
      <PhoneField value={v} onChange={setV} autoFocus />
      <EditorActions onSave={() => onSave(v)} onCancel={onCancel} pending={pending} />
    </>
  );
}

function DateEditor({
  initial,
  pending,
  onSave,
  onCancel,
}: {
  initial: string;
  pending: boolean;
  onSave: (v: string) => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  return (
    <>
      <input
        className="input num"
        type="date"
        dir="ltr"
        value={v}
        max={new Date().toISOString().slice(0, 10)}
        onChange={(e) => setV(e.target.value)}
        style={{ maxWidth: 220 }}
        autoFocus
      />
      <EditorActions onSave={() => onSave(v)} onCancel={onCancel} pending={pending} />
    </>
  );
}

function ChoiceEditor({
  value,
  options,
  pending,
  onSave,
  onCancel,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  pending: boolean;
  onSave: (v: string) => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState(value);
  return (
    <>
      <select
        className="input"
        value={v}
        onChange={(e) => setV(e.target.value)}
        style={{ maxWidth: 260 }}
        autoFocus
      >
        <option value="">— اختر —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <EditorActions onSave={() => onSave(v)} onCancel={onCancel} pending={pending} />
    </>
  );
}

/* تغييرُ البريد بتأكيد هوية: طلبُ رمزٍ ثمّ تأكيدُه. */
function EmailChangeEditor({
  currentEmail,
  onDone,
}: {
  currentEmail: string;
  onDone: () => void;
}) {
  const { reload } = useAuth();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [devCode, setDevCode] = useState<string | null>(null);

  const request = useMutation({
    mutationFn: () =>
      api<{ sent: boolean; devCode?: string }>('/auth/email-change/request', {
        method: 'POST',
        body: { newEmail: email.trim() },
      }),
    onSuccess: (r) => {
      setStep('code');
      setDevCode(r.devCode ?? null);
      toast(r.sent ? 'أُرسل رمز التأكيد إلى بريدك الجديد' : 'أُنشئ رمز التأكيد', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإرسال'),
  });

  const confirm = useMutation({
    mutationFn: () =>
      api('/auth/email-change/confirm', { method: 'POST', body: { code: code.trim() } }),
    onSuccess: async () => {
      await reload();
      toast('تغيّر بريدك بنجاح', 'success');
      onDone();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التأكيد'),
  });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && email.trim() !== currentEmail;

  if (step === 'code') {
    return (
      <>
        <p className="tiny mute" style={{ margin: '0 0 8px' }}>
          أدخل الرمز المكوّن من ستّة أرقام المُرسَل إلى{' '}
          <b className="num" dir="ltr">
            {email.trim()}
          </b>
          .
        </p>
        {devCode && (
          <p className="tiny" style={{ margin: '0 0 8px', color: 'var(--warning)' }}>
            لأغراض التطوير — لم يُضبط مُرسِلُ بريدٍ، الرمز:{' '}
            <b className="num">{devCode}</b>
          </p>
        )}
        <input
          className="input num"
          dir="ltr"
          inputMode="numeric"
          maxLength={6}
          value={code}
          placeholder="••••••"
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          style={{ maxWidth: 180, letterSpacing: '0.3em', textAlign: 'center' }}
          autoFocus
        />
        <div className="info-editor-actions">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => confirm.mutate()}
            disabled={code.trim().length !== 6 || confirm.isPending}
          >
            {confirm.isPending ? 'جارٍ التأكيد' : 'تأكيد البريد'}
          </button>
          <button
            className="btn btn-sm"
            onClick={() => setStep('email')}
            disabled={confirm.isPending}
          >
            رجوع
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <p className="tiny mute" style={{ margin: '0 0 8px' }}>
        سنرسل رمز تأكيدٍ إلى البريد الجديد لإثبات ملكيّتك له قبل التغيير.
      </p>
      <input
        className="input num"
        dir="ltr"
        type="email"
        value={email}
        placeholder="you@example.com"
        onChange={(e) => setEmail(e.target.value)}
        style={{ maxWidth: 320 }}
        autoFocus
      />
      <div className="info-editor-actions">
        <button
          className="btn btn-primary btn-sm"
          onClick={() => request.mutate()}
          disabled={!emailValid || request.isPending}
        >
          {request.isPending ? 'جارٍ الإرسال' : 'أرسل رمز التأكيد'}
        </button>
      </div>
    </>
  );
}

/* ---------------- كلمة المرور ---------------- */

export function PasswordCard() {
  const [mode, setMode] = useState<'current' | 'code'>('current');

  return (
    <div className="card">
      <div className="card-head">
        <h3 className="card-title">تغيير كلمة المرور</h3>
        <span className="seg grain" role="group" aria-label="طريقة التغيير">
          <button
            type="button"
            className={`seg-btn${mode === 'current' ? ' is-on' : ''}`}
            aria-pressed={mode === 'current'}
            onClick={() => setMode('current')}
          >
            بالكلمة الحالية
          </button>
          <button
            type="button"
            className={`seg-btn${mode === 'code' ? ' is-on' : ''}`}
            aria-pressed={mode === 'code'}
            onClick={() => setMode('code')}
          >
            برمز تأكيد
          </button>
        </span>
      </div>
      <div className="card-body">
        {mode === 'current' ? <PasswordByCurrent /> : <PasswordByCode />}
      </div>
    </div>
  );
}

/* تغييرُ الكلمة بالكلمة الحالية. */
function PasswordByCurrent() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [human, setHuman] = useState(false);

  const change = useMutation({
    mutationFn: () =>
      api<{ revokedSessions: number }>('/auth/change-password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
    },
  });

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 10;
  const same = next.length > 0 && next === current;
  const valid = current.length > 0 && next.length >= 10 && next === confirm && next !== current;

  return (
    <>
      {change.isSuccess && (
        <div className="alert alert-info mb-4">
          <Icon name="check" size={15} />
          <div>
            تم تغيير كلمة المرور.
            {change.data.revokedSessions > 0 && (
              <>
                {' '}
                وأُغلقت {plural(change.data.revokedSessions, WORDS.session)} أخرى كانت مفتوحة على
                أجهزة ثانية.
              </>
            )}
          </div>
        </div>
      )}
      {change.isError && (
        <div className="alert alert-danger mb-4">
          {change.error instanceof Error ? change.error.message : 'تعذّر التغيير.'}
        </div>
      )}

      <div className="pw-split">
        <div className="pw-fields">
          <div className="field">
            <label className="label req" htmlFor="a-current">
              كلمة المرور الحالية
            </label>
            <PasswordInput
              id="a-current"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label req" htmlFor="a-new">
              كلمة المرور الجديدة
            </label>
            <PasswordInput
              id="a-new"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <span className={`tiny ${tooShort || same ? 'neg' : 'mute'}`}>
              {same ? 'الجديدة يجب أن تختلف عن الحالية.' : 'عشرة أحرف على الأقل.'}
            </span>
          </div>
          <div className="field">
            <label className="label req" htmlFor="a-confirm">
              تأكيد كلمة المرور
            </label>
            <PasswordInput
              id="a-confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {mismatch && <span className="tiny neg">الكلمتان غير متطابقتين.</span>}
          </div>
        </div>

        <div className="pw-side">
          <HumanCheck verified={human} onChange={setHuman} />

          {human && valid && (
            <p className="tiny" style={{ color: 'var(--success)', margin: 0 }}>
              <Icon name="check" size={13} /> جاهزٌ للحفظ.
            </p>
          )}
          <button
            className="btn btn-primary"
            onClick={() => change.mutate()}
            disabled={!valid || !human || change.isPending}
          >
            {change.isPending ? 'جارٍ التغيير' : 'تغيير كلمة المرور'}
          </button>
          <p className="tiny mute" style={{ margin: 0 }}>
            تغيير كلمة المرور يُغلق جلساتك على الأجهزة الأخرى ويُبقي هذه الجلسة مفتوحة.
          </p>
        </div>
      </div>
    </>
  );
}

/**
 * تحقّقٌ بشريٌّ: لعبةُ بازل بلا خدمةٍ خارجية.
 *
 * القطعةُ مائلةٌ (مقلوبة) خارجَ مكانها؛ يسحبها المستخدمُ بالمنزلق فتنزلق
 * أفقيًّا وتعتدلُ تدريجيًّا حتى تستقرَّ في الفجوة معتدلةً — فيَثبتُ أنّه
 * إنسان. لا reCAPTCHA (محجوبةٌ ومتعقِّبة)، ولا رقمٌ يُحسب.
 */
const JIG_W = 280;
const JIG_H = 150;
const JIG_S = 46;
const JIG_Y = 54;
const JIG_TIME = 30;
const RING_R = 30;
const RING_C = 2 * Math.PI * RING_R;

function drawScene(ctx: CanvasRenderingContext2D): void {
  const g = ctx.createLinearGradient(0, 0, JIG_W, JIG_H);
  g.addColorStop(0, '#3672ea');
  g.addColorStop(1, '#6d5bd0');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, JIG_W, JIG_H);
  const cols = ['#8ce553', '#edda2e', '#ff8a5b', '#ffffff', '#0e9f9f'];
  for (let i = 0; i < 7; i++) {
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = cols[i % cols.length];
    ctx.beginPath();
    ctx.arc(Math.random() * JIG_W, Math.random() * JIG_H, 12 + Math.random() * 26, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function HumanCheck({
  verified,
  onChange,
}: {
  verified: boolean;
  onChange: (v: boolean) => void;
}) {
  const bgRef = useRef<HTMLCanvasElement>(null);
  const pieceRef = useRef<HTMLCanvasElement>(null);
  const [target, setTarget] = useState(0);
  const [x, setX] = useState(0);
  const [rot, setRot] = useState(0);
  const [failed, setFailed] = useState(false);
  const [seconds, setSeconds] = useState(30);
  const [started, setStarted] = useState(false);
  const maxX = JIG_W - JIG_S;

  const build = () => {
    const bg = bgRef.current;
    const pc = pieceRef.current;
    if (!bg || !pc) return;
    const ctx = bg.getContext('2d');
    const pctx = pc.getContext('2d');
    if (!ctx || !pctx) return;

    const t = Math.round(70 + Math.random() * (maxX - 80));
    setTarget(t);
    setX(Math.random() < 0.5 ? 0 : Math.round(Math.random() * (maxX * 0.4)));
    /* تبدأ مائلةً بوضوحٍ («مقلوبة») ليُعدّلها المستخدمُ بالعمود. */
    setRot((Math.random() < 0.5 ? -1 : 1) * Math.round(90 + Math.random() * 80));
    setFailed(false);
    setSeconds(30);
    onChange(false);

    drawScene(ctx);
    pctx.clearRect(0, 0, JIG_S, JIG_S);
    pctx.drawImage(bg, t, JIG_Y, JIG_S, JIG_S, 0, 0, JIG_S, JIG_S);
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(t, JIG_Y, JIG_S, JIG_S);
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 2;
    ctx.strokeRect(t, JIG_Y, JIG_S, JIG_S);
    ctx.restore();
  };

  /* اللعبةُ لا تُبنى إلا بعد فتحها بالزرّ (وحينها تتوفّر لوحاتُ الرسم). */
  useEffect(() => {
    if (started) build();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  /* مؤقّتٌ ثلاثون ثانية — عند انتهائه لغزٌ جديد ويعود العدّ. */
  useEffect(() => {
    if (!started || verified) return;
    if (seconds <= 0) {
      build();
      return;
    }
    const id = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, seconds, verified]);

  /* محاذاةٌ حيّة: مكانٌ صحيحٌ ووضعٌ معتدل — يظهر إطارٌ أخضر قبل «تحقّق». */
  const aligned = Math.abs(x - target) <= 8 && Math.abs(rot) <= 10;

  /* التحقّقُ يدويٌّ بالزرّ. */
  const check = () => {
    onChange(aligned);
    setFailed(!aligned);
  };

  return (
    <div className={`human-check${verified ? ' is-ok' : ''}`}>
      <div className="human-check-head">
        <Icon name={verified ? 'check' : 'target'} size={16} />
        <span>
          {verified
            ? 'تم التحقّق أنك لست روبوتًا'
            : started
              ? 'حرّك القطعة لمكانها، وأدِرها بالعمود لتعتدل، ثمّ تحقّق'
              : 'أكّد أنّك لست روبوتًا قبل الحفظ'}
        </span>
      </div>

      {!verified && !started && (
        <button type="button" className="btn btn-primary mt-3" onClick={() => setStarted(true)}>
          <Icon name="target" size={15} /> تحقّق أنّي إنسان
        </button>
      )}

      {!verified && started && (
        <div className="jig">
          <div className="jig-stage">
            <div className="jig-vp" style={{ width: JIG_W, height: JIG_H }}>
              <canvas ref={bgRef} width={JIG_W} height={JIG_H} className="jig-bg" />
              <canvas
                ref={pieceRef}
                width={JIG_S}
                height={JIG_S}
                className={`jig-piece${aligned ? ' is-aligned' : ''}`}
                style={{ top: JIG_Y, left: x, transform: `rotate(${rot}deg)` }}
              />
            </div>
            <div className="jig-side">
              {/* عمودٌ طوليٌّ لإدارة القطعة يدويًّا. */}
              <input
                type="range"
                className="jig-vert"
                min={-180}
                max={180}
                value={rot}
                onChange={(e) => {
                  setRot(Number(e.target.value));
                  setFailed(false);
                }}
                aria-label="أدِر القطعة"
              />
            </div>

            {/* مؤقّتٌ دائريٌّ متحرّكٌ يتوسّط الفراغَ — يعدّ من ثلاثين. */}
            <div className="jig-clock-slot">
              <div className={`jig-clock${seconds <= 5 ? ' is-low' : ''}`}>
                <svg viewBox="0 0 72 72" className="jig-clock-svg" aria-hidden="true">
                  <circle className="jig-clock-track" cx="36" cy="36" r={RING_R} />
                  <circle
                    className="jig-clock-fill"
                    cx="36"
                    cy="36"
                    r={RING_R}
                    style={{
                      strokeDasharray: RING_C,
                      strokeDashoffset: RING_C * (1 - seconds / JIG_TIME),
                    }}
                  />
                </svg>
                <span className="jig-clock-num num">{seconds}</span>
              </div>
            </div>
          </div>
          <input
            type="range"
            className="jig-slider"
            min={0}
            max={maxX}
            value={x}
            onChange={(e) => {
              setX(Number(e.target.value));
              setFailed(false);
            }}
            aria-label="حرّك القطعة أفقيًّا"
          />
          <div className="row mt-2" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
            <button type="button" className="btn btn-sm btn-primary" onClick={check}>
              <Icon name="check" size={14} /> تحقّق
            </button>
            {failed && <span className="tiny neg">ليست في مكانها أو غير معتدلة — عدّل ثمّ تحقّق.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/* تغييرُ الكلمة برمزٍ يصل بريد الحساب — لمن نسي كلمتَه الحالية. */
function PasswordByCode() {
  const toast = useToast();
  const [step, setStep] = useState<'start' | 'set'>('start');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState<number | null>(null);

  const request = useMutation({
    mutationFn: () =>
      api<{ sent: boolean; devCode?: string }>('/auth/password-reset/request', { method: 'POST' }),
    onSuccess: (r) => {
      setStep('set');
      setDevCode(r.devCode ?? null);
      toast(r.sent ? 'أُرسل الرمز إلى بريدك' : 'أُنشئ الرمز', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإرسال'),
  });

  const submit = useMutation({
    mutationFn: () =>
      api<{ revokedSessions: number }>('/auth/password-reset/confirm', {
        method: 'POST',
        body: { code: code.trim(), newPassword: next },
      }),
    onSuccess: (r) => {
      setDone(r.revokedSessions);
      setStep('start');
      setCode('');
      setNext('');
      setConfirm('');
      setDevCode(null);
      toast('تغيّرت كلمة المرور', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التغيير'),
  });

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 10;
  const valid = code.trim().length === 6 && next.length >= 10 && next === confirm;

  if (step === 'start') {
    return (
      <>
        {done !== null && (
          <div className="alert alert-info mb-4">
            <Icon name="check" size={15} />
            <div>
              تم تغيير كلمة المرور.
              {done > 0 && (
                <>
                  {' '}
                  وأُغلقت {plural(done, WORDS.session)} أخرى كانت مفتوحة على أجهزة ثانية.
                </>
              )}
            </div>
          </div>
        )}
        <p className="small mute" style={{ marginTop: 0 }}>
          سنرسل رمزًا من ستّة أرقام إلى بريد حسابك لإثبات هويّتك، ثمّ تعيّن كلمةً جديدةً بلا حاجةٍ
          لكلمتك الحالية.
        </p>
        <button
          className="btn btn-primary mt-3"
          onClick={() => request.mutate()}
          disabled={request.isPending}
        >
          <Icon name="mail" size={15} />
          {request.isPending ? 'جارٍ الإرسال' : 'أرسل رمز التأكيد إلى بريدي'}
        </button>
      </>
    );
  }

  return (
    <>
      {devCode && (
        <p className="tiny mb-4" style={{ color: 'var(--warning)' }}>
          لأغراض التطوير — لم يُضبط مُرسِلُ بريدٍ، الرمز: <b className="num">{devCode}</b>
        </p>
      )}
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="pc-code">
            رمز التأكيد
          </label>
          <input
            id="pc-code"
            className="input num"
            dir="ltr"
            inputMode="numeric"
            maxLength={6}
            value={code}
            placeholder="••••••"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            style={{ maxWidth: 180, letterSpacing: '0.3em', textAlign: 'center' }}
          />
        </div>
        <div className="field span-2">
          <label className="label req" htmlFor="pc-new">
            كلمة المرور الجديدة
          </label>
          <PasswordInput
            id="pc-new"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <span className={`tiny ${tooShort ? 'neg' : 'mute'}`}>عشرة أحرف على الأقل.</span>
        </div>
        <div className="field span-2">
          <label className="label req" htmlFor="pc-confirm">
            تأكيد كلمة المرور
          </label>
          <PasswordInput
            id="pc-confirm"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {mismatch && <span className="tiny neg">الكلمتان غير متطابقتين.</span>}
        </div>
      </div>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() => submit.mutate()}
          disabled={!valid || submit.isPending}
        >
          {submit.isPending ? 'جارٍ التغيير' : 'تأكيد وتغيير الكلمة'}
        </button>
        <button className="btn" onClick={() => setStep('start')} disabled={submit.isPending}>
          رجوع
        </button>
      </div>
      <p className="tiny mute mt-3">تغيير كلمة المرور يُغلق جلساتك على الأجهزة الأخرى.</p>
    </>
  );
}

/* ---------------- المصادقة الثنائية (تطبيق TOTP) ---------------- */

function TwoFactorCard() {
  const { session, reload } = useAuth();
  const toast = useToast();
  const enabled = !!session!.user.totpEnabled;
  const [mode, setMode] = useState<'idle' | 'enroll' | 'disable'>('idle');
  const [qr, setQr] = useState<string>('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');

  const start = useMutation({
    mutationFn: () => api<{ secret: string; otpauth: string }>('/auth/totp/start', { method: 'POST' }),
    onSuccess: async (r) => {
      setSecret(r.secret);
      setQr(await QRCode.toDataURL(r.otpauth, { margin: 1, width: 200 }));
      setMode('enroll');
      setCode('');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر البدء'),
  });

  const enable = useMutation({
    mutationFn: () => api('/auth/totp/enable', { method: 'POST', body: { code: code.trim() } }),
    onSuccess: async () => {
      await reload();
      setMode('idle');
      setCode('');
      toast('فُعّلت المصادقة الثنائية', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'الرمز غير صحيح'),
  });

  const disable = useMutation({
    mutationFn: () => api('/auth/totp/disable', { method: 'POST', body: { code: code.trim() } }),
    onSuccess: async () => {
      await reload();
      setMode('idle');
      setCode('');
      toast('عُطّلت المصادقة الثنائية', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'الرمز غير صحيح'),
  });

  return (
    <div className="card">
      <div className="card-head">
        <h3 className="card-title">
          <Icon name="phone" size={16} /> المصادقة الثنائية (تطبيق المصادقة)
        </h3>
        {enabled ? (
          <Badge kind="success">
            <Icon name="check" size={12} /> مفعّلة
          </Badge>
        ) : (
          <Badge kind="neutral">غير مفعّلة</Badge>
        )}
      </div>
      <div className="card-body">
        {mode === 'idle' && (
          <>
            <p className="small mute" style={{ marginTop: 0 }}>
              استخدم تطبيق مصادقةٍ (Google Authenticator أو غيره) ليولّد رمزًا كلَّ ثلاثين ثانية —
              فتؤكّد دخولك بلا حاجةٍ لرسالةِ بريد.
            </p>
            {enabled ? (
              <button className="btn mt-3" onClick={() => setMode('disable')}>
                <Icon name="x" size={15} /> تعطيل المصادقة الثنائية
              </button>
            ) : (
              <button
                className="btn btn-primary mt-3"
                onClick={() => start.mutate()}
                disabled={start.isPending}
              >
                <Icon name="phone" size={15} />
                {start.isPending ? 'جارٍ التجهيز' : 'تفعيل بتطبيق المصادقة'}
              </button>
            )}
          </>
        )}

        {mode === 'enroll' && (
          <div className="totp-enroll">
            <div className="totp-qr">
              {qr && <img src={qr} alt="رمز التفعيل" width={180} height={180} />}
            </div>
            <div className="totp-steps">
              <p className="small" style={{ marginTop: 0 }}>
                ١) امسح الرمز بتطبيق المصادقة، أو أدخل المفتاح يدويًّا:
              </p>
              <code className="totp-secret num" dir="ltr">
                {secret.replace(/(.{4})/g, '$1 ').trim()}
              </code>
              <p className="small mb-2">٢) أدخل الرمز الظاهر في التطبيق للتأكيد:</p>
              <input
                className="input num"
                dir="ltr"
                inputMode="numeric"
                maxLength={6}
                value={code}
                placeholder="••••••"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                style={{ maxWidth: 160, letterSpacing: '0.3em', textAlign: 'center' }}
              />
              <div className="row mt-3" style={{ gap: 'var(--sp-2)' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => enable.mutate()}
                  disabled={code.trim().length !== 6 || enable.isPending}
                >
                  {enable.isPending ? 'جارٍ التفعيل' : 'تأكيد وتفعيل'}
                </button>
                <button className="btn" onClick={() => setMode('idle')} disabled={enable.isPending}>
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        )}

        {mode === 'disable' && (
          <>
            <p className="small mute" style={{ marginTop: 0 }}>
              أدخل رمزًا من تطبيق المصادقة لتأكيد التعطيل.
            </p>
            <input
              className="input num"
              dir="ltr"
              inputMode="numeric"
              maxLength={6}
              value={code}
              placeholder="••••••"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{ maxWidth: 160, letterSpacing: '0.3em', textAlign: 'center' }}
            />
            <div className="row mt-3" style={{ gap: 'var(--sp-2)' }}>
              <button
                className="btn btn-danger"
                onClick={() => disable.mutate()}
                disabled={code.trim().length !== 6 || disable.isPending}
              >
                {disable.isPending ? 'جارٍ التعطيل' : 'تعطيل'}
              </button>
              <button className="btn" onClick={() => setMode('idle')} disabled={disable.isPending}>
                إلغاء
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- بريد الاسترداد ---------------- */

function RecoveryEmailCard() {
  const { session, reload } = useAuth();
  const toast = useToast();
  const current = session!.user.recoveryEmail ?? null;
  const [mode, setMode] = useState<'idle' | 'email' | 'code'>('idle');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);

  const request = useMutation({
    mutationFn: () =>
      api<{ sent: boolean; devCode?: string }>('/auth/recovery-email/request', {
        method: 'POST',
        body: { email: email.trim() },
      }),
    onSuccess: (r) => {
      setDevCode(r.devCode ?? null);
      setMode('code');
      toast(r.sent ? 'أُرسل الرمز إلى بريد الاسترداد' : 'أُنشئ الرمز', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإرسال'),
  });

  const confirm = useMutation({
    mutationFn: () =>
      api('/auth/recovery-email/confirm', { method: 'POST', body: { code: code.trim() } }),
    onSuccess: async () => {
      await reload();
      setMode('idle');
      setEmail('');
      setCode('');
      toast('حُفظ بريد الاسترداد', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'الرمز غير صحيح'),
  });

  const remove = useMutation({
    mutationFn: () => api('/auth/recovery-email/remove', { method: 'POST' }),
    onSuccess: async () => {
      await reload();
      toast('أُزيل بريد الاسترداد', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف'),
  });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <div className="card">
      <div className="card-head">
        <h3 className="card-title">
          <Icon name="mailOpen" size={16} /> بريد الاسترداد
        </h3>
        {current && <Badge kind="success">مضبوط</Badge>}
      </div>
      <div className="card-body">
        {mode === 'idle' && (
          <>
            <p className="small mute" style={{ marginTop: 0 }}>
              بريدٌ بديلٌ نرسل إليه رمزَ استرداد كلمة المرور لو فقدت الوصول إلى بريدك الأساسيّ.
            </p>
            {current ? (
              <div className="row mt-3" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
                <span className="num" dir="ltr" style={{ fontWeight: 600 }}>
                  {current}
                </span>
                <button className="btn btn-sm" onClick={() => setMode('email')}>
                  <Icon name="edit" size={14} /> تغيير
                </button>
                <button
                  className="btn btn-sm"
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                >
                  <Icon name="trash" size={14} /> إزالة
                </button>
              </div>
            ) : (
              <button className="btn btn-primary mt-3" onClick={() => setMode('email')}>
                <Icon name="plus" size={15} /> أضف بريد استرداد
              </button>
            )}
          </>
        )}

        {mode === 'email' && (
          <>
            <label className="label req">البريد البديل</label>
            <input
              className="input num"
              dir="ltr"
              type="email"
              value={email}
              placeholder="backup@example.com"
              onChange={(e) => setEmail(e.target.value)}
              style={{ maxWidth: 340 }}
              autoFocus
            />
            <div className="row mt-3" style={{ gap: 'var(--sp-2)' }}>
              <button
                className="btn btn-primary"
                onClick={() => request.mutate()}
                disabled={!emailValid || request.isPending}
              >
                {request.isPending ? 'جارٍ الإرسال' : 'أرسل رمز التأكيد'}
              </button>
              <button className="btn" onClick={() => setMode('idle')} disabled={request.isPending}>
                إلغاء
              </button>
            </div>
          </>
        )}

        {mode === 'code' && (
          <>
            <p className="small mute" style={{ marginTop: 0 }}>
              أدخل الرمز المُرسَل إلى{' '}
              <b className="num" dir="ltr">
                {email.trim()}
              </b>
              .
            </p>
            {devCode && (
              <p className="tiny mb-2" style={{ color: 'var(--warning)' }}>
                لأغراض التطوير — الرمز: <b className="num">{devCode}</b>
              </p>
            )}
            <input
              className="input num"
              dir="ltr"
              inputMode="numeric"
              maxLength={6}
              value={code}
              placeholder="••••••"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{ maxWidth: 160, letterSpacing: '0.3em', textAlign: 'center' }}
              autoFocus
            />
            <div className="row mt-3" style={{ gap: 'var(--sp-2)' }}>
              <button
                className="btn btn-primary"
                onClick={() => confirm.mutate()}
                disabled={code.trim().length !== 6 || confirm.isPending}
              >
                {confirm.isPending ? 'جارٍ التأكيد' : 'تأكيد البريد'}
              </button>
              <button className="btn" onClick={() => setMode('email')} disabled={confirm.isPending}>
                رجوع
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- حذف الحساب نهائيًّا ---------------- */

function DeleteAccountCard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [ack, setAck] = useState(false);
  const [password, setPassword] = useState('');

  const del = useMutation({
    mutationFn: () => api('/settings/account/delete', { method: 'POST', body: { password } }),
    onSuccess: async () => {
      toast('حُذف الحساب نهائيًّا', 'success');
      await logout();
      navigate('/login');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف'),
  });

  return (
    <div className="card" style={{ borderColor: 'var(--danger)' }}>
      <div className="card-head">
        <h3 className="card-title" style={{ color: 'var(--danger)' }}>
          <Icon name="alert" size={16} /> حذف الحساب نهائيًّا
        </h3>
      </div>
      <div className="card-body">
        <div className="alert alert-danger">
          <Icon name="alert" size={15} />
          <div>
            <b>هذا إجراءٌ نهائيٌّ لا رجعةَ فيه.</b> بمجرّد الحذف تُمحى جميعُ بياناتك وبيانات مؤسّستك
            بشكلٍ دائم، ويتوقّف وصولُ كلّ المستخدمين فورًا، ولا يمكن استرجاعُ أيِّ شيء.
          </div>
        </div>

        <label className="ack-line">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          <span>
            أُقِرّ بأن الحذف نهائيّ، وأن كلّ بياناتي وبيانات مؤسّستي ستُحذف بشكلٍ دائم، وأنني لا
            أملك حقّ المطالبة بأيّ مبالغ ماليّة بعد الحذف.
          </span>
        </label>

        <div className="field mt-3" style={{ maxWidth: 340 }}>
          <label className="label req">كلمة مرورك للتأكيد</label>
          <PasswordInput
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <button
          className="btn btn-danger mt-3"
          disabled={!ack || password.length < 1 || del.isPending}
          onClick={() => {
            if (window.confirm('تأكيدٌ أخير: حذفُ الحساب وكلِّ بياناته نهائيًّا؟ لا رجعةَ بعدها.')) {
              del.mutate();
            }
          }}
        >
          {del.isPending ? 'جارٍ الحذف' : 'حذف حسابي وكلّ بياناته نهائيًّا'}
        </button>
      </div>
    </div>
  );
}
